const VOL = { tsp: 4.929, tbsp: 14.787, "fl oz": 29.574, cup: 236.59, pint: 473.18, quart: 946.35, gallon: 3785.4, ml: 1, l: 1000 };
const WT = { g: 1, kg: 1000, oz: 28.35, lb: 453.59 };
const ALIAS = {
  teaspoon: "tsp", teaspoons: "tsp", tsp: "tsp", tsps: "tsp", t: "tsp",
  tablespoon: "tbsp", tablespoons: "tbsp", tbsp: "tbsp", tbsps: "tbsp", tbs: "tbsp", tb: "tbsp", T: "tbsp",
  cup: "cup", cups: "cup", c: "cup", "fl oz": "fl oz", "fl. oz": "fl oz", floz: "fl oz",
  pint: "pint", pints: "pint", pt: "pint", quart: "quart", quarts: "quart", qt: "quart", gallon: "gallon", gallons: "gallon", gal: "gallon",
  ml: "ml", milliliter: "ml", milliliters: "ml", millilitre: "ml", millilitres: "ml", l: "l", liter: "l", liters: "l", litre: "l", litres: "l",
  g: "g", gram: "g", grams: "g", kg: "kg", kilogram: "kg", kilograms: "kg", oz: "oz", ounce: "oz", ounces: "oz", lb: "lb", lbs: "lb", pound: "lb", pounds: "lb",
};
const UNI = { "¼": 0.25, "½": 0.5, "¾": 0.75, "⅓": 1 / 3, "⅔": 2 / 3, "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875 };
const FRACS = [[0, ""], [1 / 8, "1/8"], [1 / 4, "1/4"], [1 / 3, "1/3"], [3 / 8, "3/8"], [1 / 2, "1/2"], [5 / 8, "5/8"], [2 / 3, "2/3"], [3 / 4, "3/4"], [7 / 8, "7/8"], [1, ""]];

export const unitOf = (u) => { const s = String(u || "").trim().replace(/\.$/, ""); return ALIAS[s] || ALIAS[s.toLowerCase()] || null; };

const num1 = (s) => {
  s = s.trim();
  if (!s) return null;
  let total = 0;
  for (const part of s.split(/\s+/)) {
    if (UNI[part] != null) total += UNI[part];
    else if (/^\d+[¼½¾⅓⅔⅛⅜⅝⅞]$/.test(part)) total += Number(part.slice(0, -1)) + UNI[part.slice(-1)];
    else if (/^\d+\/\d+$/.test(part)) { const [a, b] = part.split("/").map(Number); if (!b) return null; total += a / b; }
    else if (/^\d*\.?\d+$/.test(part)) total += Number(part);
    else return null;
  }
  return total;
};

export const parseQty = (q) => {
  const s = String(q || "").trim();
  const range = s.split(/\s*(?:-|–|to)\s*/);
  if (range.length === 2) { const a = num1(range[0]), b = num1(range[1]); if (a != null && b != null) return [a, b]; }
  const v = num1(s);
  return v == null ? null : [v];
};

export const fmtNum = (n) => {
  if (n >= 20) return String(Math.round(n));
  const whole = Math.floor(n);
  const rest = n - whole;
  const [f, label] = FRACS.reduce((best, x) => (Math.abs(x[0] - rest) < Math.abs(best[0] - rest) ? x : best));
  if (Math.abs(f - rest) > 0.04) return String(Math.round(n * 10) / 10);
  const w = f === 1 ? whole + 1 : whole;
  return [w || (!label ? "0" : ""), label].filter(Boolean).join(" ");
};

const clean = (v) => Math.abs(Number(fmtNum(v).split(" ").reduce((a, p) => a + (num1(p) || 0), 0)) - v) < 0.02 * Math.max(1, v);
const bestUs = (ml) => {
  const opts = [["cup", 236.59 / 4], ["tbsp", 14.787], ["tsp", 0]].filter(([, min]) => ml >= min);
  return (opts.find(([u]) => clean(ml / VOL[u])) || opts[Math.min(1, opts.length - 1)])[0];
};
const pick = (unit, base) => {
  if (unit in WT) return unit === "g" || unit === "kg" ? (base >= 1000 ? "kg" : "g") : base >= 453.59 ? "lb" : "oz";
  if (unit === "ml" || unit === "l") return base >= 1000 ? "l" : "ml";
  if (unit === "fl oz" || unit === "pint" || unit === "quart" || unit === "gallon") return base >= 3785.4 ? "gallon" : base >= 946.35 ? "quart" : unit === "fl oz" ? "fl oz" : "pint";
  return bestUs(base);
};

const metricFmt = (v) => String(v >= 100 ? Math.round(v) : v >= 10 ? Math.round(v * 10) / 10 : Math.round(v * 100) / 100);

export const scaleIngredient = (ing, factor) => {
  const q = parseQty(ing.qty);
  if (!q || factor === 1) return { qty: ing.qty, unit: ing.unit, changed: false };
  const u = unitOf(ing.unit);
  const scaled = q.map((x) => x * factor);
  if (!u) return { qty: scaled.map(fmtNum).join("-"), unit: ing.unit, changed: true };
  const table = u in WT ? WT : VOL;
  const base = scaled.map((x) => x * table[u]);
  const to = pick(u, Math.max(...base));
  const vals = base.map((b) => b / table[to]);
  const metric = ["g", "kg", "ml", "l"].includes(to);
  return { qty: vals.map(metric ? metricFmt : fmtNum).join("-"), unit: to, changed: true };
};

const PLURAL = { cup: "cups", pint: "pints", quart: "quarts", gallon: "gallons" };
export const unitLabel = (unit, qty) => { const top = String(qty).split("-").pop().trim(); return PLURAL[unit] && !/^(1|0|\d\/\d|0?\.\d+)$/.test(top) ? PLURAL[unit] : unit; };

export const lineText = (qty, unit, ing) => [qty, unit, ing.name].filter(Boolean).join(" ") + (ing.note ? `, ${ing.note}` : "");

export const newIngId = () => `i${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
